import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Edit, Trash2, Calendar, FileText, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { deleteReviewDraft } from '@/lib/api/reviews';

const DraftReviewsSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: draftReviews, isLoading, error } = useQuery({
    queryKey: ['draft-reviews', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          papers (
            id,
            current_version,
            paper_versions (
              title,
              abstract
            )
          )
        `)
        .eq('reviewer_id', user.id)
        .eq('status', 'draft')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id
  });

  const deleteMutation = useMutation({
    mutationFn: (reviewId: string) => deleteReviewDraft(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draft-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Draft deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete draft');
    }
  });

  const handleEdit = (paperId: string, reviewId: string) => {
    navigate(`/review/${paperId}/${reviewId}`);
  };

  const handleDelete = (reviewId: string) => {
    if (window.confirm('Are you sure you want to delete this draft? This action cannot be undone.')) {
      deleteMutation.mutate(reviewId);
    }
  };

  const handleViewPaper = (paperId: string) => {
    navigate(`/paper/${paperId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse border-gray-100">
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-red-700 font-medium">Error loading draft reviews</p>
          <p className="text-red-600 text-sm mt-1">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  if (!draftReviews?.length) {
    return (
      <div className="text-center py-16">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 max-w-md mx-auto">
          <div className="p-3 bg-amber-100 rounded-full w-fit mx-auto mb-4">
            <FileText className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold text-amber-900 mb-2">No draft reviews</h3>
          <p className="text-amber-700 mb-4">
            You don't have any draft reviews yet. Start reviewing papers to see your drafts here.
          </p>
          <Button 
            onClick={() => navigate('/explore')} 
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Explore Papers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {draftReviews.map((review) => {
        const paper = review.papers;
        const currentVersion = paper?.paper_versions?.[0];

        return (
          <Card key={review.id} className="border-l-4 border-l-amber-400 hover:shadow-md transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle 
                    className="text-xl mb-3 line-clamp-2 text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() => handleViewPaper(paper?.id)}
                  >
                    {currentVersion?.title || 'Untitled Paper'}
                  </CardTitle>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>Last updated: {new Date(review.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                  Draft
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="py-4">
              <p className="text-gray-700 line-clamp-3 mb-4 leading-relaxed">
                {currentVersion?.abstract || 'No abstract available'}
              </p>
              
              {review.recommendation && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Recommendation:</span>
                  <Badge variant="outline" className="capitalize font-medium">
                    {review.recommendation}
                  </Badge>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex gap-2 pt-4 bg-gray-50/50 justify-end">
              <Button
                onClick={() => handleEdit(paper?.id, review.id)}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Edit className="h-4 w-4 mr-2" />
                Continue Editing
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(review.id)}
                disabled={deleteMutation.isPending}
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
};

export default DraftReviewsSection;
